import React from 'react';
import { Alert, AlertTitle, Stack, Typography } from '@mui/material';

import { Button } from '../../components/core';
import { useDeployShopifyAppMutation } from '../../store/modules/ecomConnector/api';
import type { ShopifyOnboardingStep } from '../../store/modules/merchant/types';
import { toast } from '../../util/toast';
import { buttonPaddingSx, getApiErrorMessage } from './utils';

/** A deploy took about 112 seconds in dev, so the wait is worth stating rather than leaving blank. */
const EXPECTED_DURATION = 'about two minutes';

type Props = {
  merchantId: string;
  step?: ShopifyOnboardingStep;
  clientId?: string;
  automationToken?: string;
  appName: string;
  isTokenExpired: boolean;
  isUnlocked: boolean;
  onStarted: () => void;
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

export const ShopifyDeployStep = ({
  merchantId,
  step,
  clientId,
  automationToken,
  appName,
  isTokenExpired,
  isUnlocked,
  onStarted,
}: Props) => {
  const [deployShopifyApp, { isLoading: isStarting }] = useDeployShopifyAppMutation();

  const status = step?.status ?? 'pending';
  const isRunning = status === 'in_progress';
  const versionName = readString(step?.meta?.versionName);
  const executionId = readString(step?.meta?.executionId);
  const error = readString(step?.meta?.error);

  const canDeploy = isUnlocked && Boolean(clientId) && Boolean(automationToken) && !isTokenExpired;

  const deploy = async () => {
    try {
      const accepted = await deployShopifyApp({ merchantId }).unwrap();
      toast.success(
        accepted.status === 'done'
          ? 'That deploy had already finished. The step is settled, and nothing new was started.'
          : `Deploying ${accepted.appName} as ${accepted.versionName}.`,
      );
    } catch (apiError) {
      toast.error(getApiErrorMessage(apiError, 'Unable to start the deploy.'));
    } finally {
      // Even a rejected deploy can have moved the step: the connector reconciles a stranded run on
      // its way in, which is exactly when a card that had been spinning forever clears.
      onStarted();
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        Publish the first app version carrying the Skipify theme embed.
      </Typography>

      {isTokenExpired && (
        <Alert severity="warning">
          The stored automation token has expired. Rotate it on Create app first, or the deploy will
          fail on it.
        </Alert>
      )}

      {!isTokenExpired && !automationToken && (
        <Alert severity="info">
          Deploy needs a Partner client ID and an App Automation Token. Add them on Create app
          first.
        </Alert>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          text={status === 'done' ? 'Redeploy' : 'Deploy'}
          sx={buttonPaddingSx}
          onClick={deploy}
          disabled={!canDeploy || isStarting || isRunning}
          loading={isStarting || isRunning}
        />
        {isRunning && (
          <Typography variant="body2" color="text.secondary">
            Deploying {versionName ? `${versionName} ` : ''}now, {EXPECTED_DURATION}. Use Check
            Status to pick up the result
            {executionId ? `. Execution ${executionId}` : ''}
          </Typography>
        )}
      </Stack>

      {status === 'done' && versionName && (
        <Alert severity="success">
          Published <strong>{versionName}</strong> to {appName}.
        </Alert>
      )}

      {status === 'failed' && (
        <Alert severity="error">
          <AlertTitle>The last deploy failed</AlertTitle>
          <Typography variant="body2">
            {error ?? 'The job reported no reason. Check the Cloud Run logs for this execution.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            If the token was the problem, generate a new App Automation Token in the Shopify Dev
            Dashboard and save it with Rotate Partner keys, then deploy again.
          </Typography>
        </Alert>
      )}

      {!isUnlocked && (
        <Typography color="text.secondary" variant="body2">
          Complete Distribution and allowlist first.
        </Typography>
      )}
    </Stack>
  );
};
